import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import { useAuth } from '@/contexts/auth-context';
import type {
  PaginatedResponse,
  WorkHistoryEntry,
  WorkStep,
  WorkStepTemplate,
  WorkStepTemplateItem,
  WorkTask,
} from '@/lib/types';

const WORK_API = '/api/v1/work';

export interface WorkTemplateFilters {
  page?: number;
  page_size?: number;
  q?: string;
  ordering?: string;
}

export interface CreateWorkTemplatePayload {
  name: string;
  description?: string;
  items: Omit<WorkStepTemplateItem, 'id' | 'organization' | 'template' | 'created_at' | 'updated_at'>[];
}

export interface InstantiateTemplatePayload {
  project: number;
  title?: string;
}

export function useWorkTemplates(filters: WorkTemplateFilters = {}, enabled = true) {
  const clean = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v !== '' && v !== undefined)
  );
  return useQuery({
    queryKey: queryKeys.work.templateList(clean),
    queryFn: async (): Promise<PaginatedResponse<WorkStepTemplate>> => {
      const response = await apiClient.get<PaginatedResponse<WorkStepTemplate>>(
        `${WORK_API}/step-templates/`,
        { params: clean }
      );
      return response.data;
    },
    enabled,
  });
}

export function useWorkTemplate(id: number | 'new') {
  return useQuery({
    queryKey: queryKeys.work.templateDetail(id),
    queryFn: async (): Promise<WorkStepTemplate> => {
      const response = await apiClient.get<WorkStepTemplate>(`${WORK_API}/step-templates/${id}/`);
      return response.data;
    },
    enabled: typeof id === 'number' && id > 0,
  });
}

export function useCreateWorkTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateWorkTemplatePayload): Promise<WorkStepTemplate> => {
      const response = await apiClient.post<WorkStepTemplate>(`${WORK_API}/step-templates/`, data);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.work.templateLists() });
    },
  });
}

export function useUpdateWorkTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<CreateWorkTemplatePayload> }): Promise<WorkStepTemplate> => {
      const response = await apiClient.patch<WorkStepTemplate>(`${WORK_API}/step-templates/${id}/`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      qc.setQueryData(queryKeys.work.templateDetail(variables.id), data);
      qc.invalidateQueries({ queryKey: queryKeys.work.templateLists() });
    },
  });
}

export function useDeleteWorkTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`${WORK_API}/step-templates/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.work.templateLists() });
    },
  });
}

export function useInstantiateWorkTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: InstantiateTemplatePayload }): Promise<WorkTask> => {
      const response = await apiClient.post<WorkTask>(`${WORK_API}/step-templates/${id}/instantiate/`, data);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.work.taskLists() });
    },
  });
}

export interface WorkTaskFilters {
  page?: number;
  page_size?: number;
  q?: string;
  project?: number | '';
  status?: WorkTask['status'] | '';
  assigned_to?: number | '';
  ordering?: string;
}

export interface CreateWorkTaskPayload {
  project: number;
  title: string;
  description?: string;
  assigned_to?: number | null;
  status?: WorkTask['status'];
}

export interface UpdateWorkTaskPayload {
  project?: number;
  title?: string;
  description?: string;
  assigned_to?: number | null;
  status?: WorkTask['status'];
}

export function useWorkTasks(filters: WorkTaskFilters = {}, enabled = true) {
  const clean = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v !== '' && v !== undefined)
  );
  return useQuery({
    queryKey: queryKeys.work.taskList(clean),
    queryFn: async (): Promise<PaginatedResponse<WorkTask>> => {
      const response = await apiClient.get<PaginatedResponse<WorkTask>>(
        `${WORK_API}/tasks/`,
        { params: clean }
      );
      return response.data;
    },
    enabled,
  });
}

export function useWorkTask(id: number | 'new', enabled = true) {
  return useQuery({
    queryKey: queryKeys.work.taskDetail(id),
    queryFn: async (): Promise<WorkTask> => {
      const response = await apiClient.get<WorkTask>(`${WORK_API}/tasks/${id}/`);
      return response.data;
    },
    enabled: typeof id === 'number' && id > 0 && enabled,
  });
}

export function useCreateWorkTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateWorkTaskPayload): Promise<WorkTask> => {
      const response = await apiClient.post<WorkTask>(`${WORK_API}/tasks/`, data);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.work.taskLists() });
    },
  });
}

export function useUpdateWorkTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<UpdateWorkTaskPayload> }): Promise<WorkTask> => {
      const response = await apiClient.patch<WorkTask>(`${WORK_API}/tasks/${id}/`, data);
      return response.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.work.taskDetail(data.id), data);
      qc.invalidateQueries({ queryKey: queryKeys.work.taskLists() });
    },
  });
}

export function useWorkSteps(taskId: number | 'new', enabled = true) {
  return useQuery({
    queryKey: queryKeys.work.taskSteps(taskId),
    queryFn: async (): Promise<WorkStep[]> => {
      const response = await apiClient.get<PaginatedResponse<WorkStep>>(
        `${WORK_API}/tasks/${taskId}/steps/`,
        { params: { page_size: 1000 } }
      );
      return response.data.results;
    },
    enabled: typeof taskId === 'number' && taskId > 0 && enabled,
  });
}

export interface UpdateWorkStepPayload {
  result?: WorkStep['result'];
  notes?: string;
  zone?: number | null;
  spot?: number | null;
}

export function useUpdateWorkStep(taskId: number) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const stepsKey = queryKeys.work.taskSteps(taskId);
  return useMutation({
    mutationFn: async ({ stepId, data }: { stepId: number; data: Partial<UpdateWorkStepPayload> }): Promise<WorkStep> => {
      const response = await apiClient.patch<WorkStep>(
        `${WORK_API}/tasks/${taskId}/steps/${stepId}/`,
        data
      );
      return response.data;
    },
    onMutate: async ({ stepId, data }) => {
      await qc.cancelQueries({ queryKey: stepsKey });
      const previousSteps = qc.getQueryData<WorkStep[]>(stepsKey);
      if (previousSteps) {
        const now = new Date().toISOString();
        const nextSteps = previousSteps.map((step) => {
          if (step.id !== stepId) return step;
          const result = data.result ?? step.result;
          const resultChanged = data.result !== undefined;
          const completed = result !== 'pending';
          return {
            ...step,
            ...data,
            result,
            completed_by: resultChanged
              ? completed
                ? (user?.id ?? step.completed_by)
                : null
              : step.completed_by,
            completed_by_username: resultChanged
              ? completed
                ? (user?.username ?? step.completed_by_username)
                : null
              : step.completed_by_username,
            completed_at: resultChanged ? (completed ? now : null) : step.completed_at,
          };
        });
        qc.setQueryData<WorkStep[]>(stepsKey, nextSteps);
      }
      return { previousSteps };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousSteps) {
        qc.setQueryData(stepsKey, context.previousSteps);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: stepsKey });
      qc.invalidateQueries({ queryKey: queryKeys.work.taskDetail(taskId) });
      qc.invalidateQueries({ queryKey: queryKeys.work.taskLists() });
      qc.invalidateQueries({ queryKey: queryKeys.work.history({}) });
    },
  });
}

export interface WorkHistoryFilters {
  page?: number;
  page_size?: number;
  zone?: number;
  spot?: number;
}

export function useWorkHistory(filters: WorkHistoryFilters = {}, enabled = true) {
  const clean = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v !== '' && v !== undefined && v !== null)
  );
  return useQuery({
    queryKey: queryKeys.work.history(clean),
    queryFn: async (): Promise<PaginatedResponse<WorkHistoryEntry>> => {
      const response = await apiClient.get<PaginatedResponse<WorkHistoryEntry>>(
        `${WORK_API}/tasks/work-history/`,
        { params: clean }
      );
      return response.data;
    },
    enabled,
  });
}
